import { sumArray } from "common/decimal";
import Contract, { ContractType } from "domain/contract";
import contractTypeHelper from "domain/contractTypeHelper";
import { DeliveryMethod } from "domain/deliveryMethod";
import { OfferTypeEnum } from "domain/offerType";
import { UserConfiguration } from "features/login/loggedInUser";
import { Offering } from "features/settings/offerings/offering";
import * as NP from "number-precision";

import { DisplayContractsEnum, DisplayContractsType } from 'features/contracts/displayContractsEnum';
import { ContractTableType } from 'features/contracts/ContractTable';

export function allowEdit(contract: Contract, userConfiguration: UserConfiguration): boolean {
    if ((contract.customerWillSell && !userConfiguration.canSell) ||
        (!contract.customerWillSell && !userConfiguration.canBuy)) {
        return false;
    }

    if (!isContractTypeAllowed(contract.contractType, userConfiguration)) {
        return false;
    }

    if (contract.isReadOnly) return false;

    return true;
}

export function allowConvertContract(contract: Contract, isCustomer: boolean): boolean {
    return !isCustomer && contract.contractType === ContractType.BushelOnly;
}

export function allowAddOfferOrContract(contract: Contract, offerings: Offering[], isCustomer: boolean,
    userConfiguration: UserConfiguration): boolean {
    if (!userConfiguration.canMakeOffer) {
        return false;
    }

    if (!isCustomer && !isContractTypeAllowed(contract.contractType, userConfiguration)) {
        return false;
    }

    if (contract.contractType === ContractType.DelayedPrice && (!isCustomer ||
        (offerings.some(o => o.location.id === contract.location?.id && o.commodity.id === contract.commodity.id && o.contractTypes.includes(ContractType.DelayedPrice)))
    )) {
        return true;
    }
    return false;
}

export function allowSetBasis(contract: Contract, offerings: Offering[], isCustomer: boolean,
    userConfiguration: UserConfiguration, allowContractPricing: boolean): boolean {
    if (!userConfiguration.canMakeOffer) return false;

    if (contract.disablePricings && !allowContractPricing) return false;

    if (!isCustomer && !isContractTypeAllowed(ContractType.Cash, userConfiguration)) {
        return false;
    }

    if (contractTypeHelper.isHta(contract.contractType) && contract.remainingQuantity !== 0 && (!isCustomer ||
        (offerings.some(o => o.location.id === contract.location?.id && o.commodity.id === contract.commodity.id &&
            o.offerTypes.includes(OfferTypeEnum.FixBasisOnHta))))) {
        return true;
    }
    return false;
}

export function allowAddBasisOffer(
    contract: Contract,
    offerings: Offering[],
    isCustomer: boolean,
    userConfiguration: UserConfiguration,
    basisOffersEnabled: boolean,
): boolean {
    if (!basisOffersEnabled) return false;

    if (!userConfiguration.canMakeOffer) return false;

    if (!isCustomer && !isContractTypeAllowed(ContractType.Basis, userConfiguration)) {
        return false;
    }

    if (
        contractTypeHelper.isHta(contract.contractType) &&
        contract.remainingQuantity !== 0 &&
        (
            // if customer, need BasisOffer in offerings
            !isCustomer ||
            (offerings.some(o => (
                o.location.id === contract.location?.id &&
                o.commodity.id === contract.commodity.id &&
                o.offerTypes.includes(OfferTypeEnum.BasisOffer)
            )))
        )
    ) {
        return true;
    }

    return false;
}


export function allowSetFutures(contract: Contract, offerings: Offering[], isCustomer: boolean,
    userConfiguration: UserConfiguration, allowContractPricing: boolean): boolean {
    if (!userConfiguration.canMakeOffer) return false;
    if (contract.disablePricings && !allowContractPricing) return false;

    if (!isCustomer && !isContractTypeAllowed(ContractType.Cash, userConfiguration)) {
        return false;
    }

    if (contract.contractType === ContractType.Basis &&
        (!isCustomer || (offerings.some(o => o.location.id === contract.location?.id && o.commodity.id === contract.commodity.id &&
            o.offerTypes.includes(OfferTypeEnum.FixFuturesOnBasis))))) {
        return true;
    }
    return false;
}

export function isCashContractWithOpenStorage(contract: Contract) {
    return contractTypeHelper.isCash(contract.contractType) && contract.deliveryMethod === DeliveryMethod.OpenStorage;
}

export function getTotalFees(contract: Contract) {
    const extendedFeesSum = sumArray(contract.fees?.map(m => m.value ?? 0));
    const totalFees = NP.plus(extendedFeesSum, contract.freightFee ?? 0);
    return totalFees;
}

export function isContractTypeAllowed(contractType: ContractType, userConfiguration: UserConfiguration) {
    return userConfiguration.allowedContractTypes.includes(contractType)
}

export function isRollAllowed(isCustomer: boolean, isBasisOrHta: boolean, contractTableType: ContractTableType, canPlaceHedge: boolean, canEditContracts: boolean, canPushBasis: boolean): boolean {
    if (!isCustomer && isBasisOrHta) {
        switch (contractTableType) {
            case ContractTableType.Hta:
                return canPlaceHedge;
            case ContractTableType.Basis:
                return canEditContracts && canPushBasis;
        }
    }
    return false;
}

export function filterContractsByDisplayContractsOption(contracts: Contract[], selectedDisplayContractsOption: DisplayContractsType): Contract[] {
    const displayContract = selectedDisplayContractsOption?.id ?? DisplayContractsEnum.withNonZeroQuantities;
    const quantityGetter = (m: Contract) =>
        contractTypeHelper.isCash(m?.contractType) ? m?.quantityPriced : m?.remainingQuantity;

    switch (displayContract) {
        case DisplayContractsEnum.withZeroQuantities:
            contracts = contracts.filter(x => quantityGetter(x) === 0 && !x.isArchived);
            break;
        case DisplayContractsEnum.withNonZeroQuantities:
            contracts = contracts.filter(x => quantityGetter(x) !== 0 && !x.isArchived);
            break;
        case DisplayContractsEnum.archived:
            contracts = contracts.filter(x => x.isArchived);
            break;
    }

    return contracts;
}

export function filterWithOptionsContractsByContractType(contracts: Contract[], contractTableType: ContractTableType, contractType: ContractType) {
    const AllContractTypes: ContractType = 0 as ContractType;

    if (contractTableType === ContractTableType.WithOptions && contractType !== AllContractTypes) {
        return contracts?.filter(m => m.contractType === contractType);
    }

    return contracts;
}

export function toContractTableTypeFromContractType(type: ContractType): ContractTableType {
    switch (type) {
        case ContractType.Basis:
            return ContractTableType.Basis;
        case ContractType.Cash:
            return ContractTableType.Cash;
        case ContractType.Hta:
            return ContractTableType.Hta;
        case ContractType.DelayedPrice:
            return ContractTableType.DelayedPrice;
        case ContractType.BushelOnly:
            return ContractTableType.BushelOnly;
        case ContractType.CashMinPrice:
        case ContractType.HtaMinPrice:
            return ContractTableType.WithOptions;
        default:
            return null;
    }
}